import { Outlet, useLocation } from 'react-router';
import { ToastProvider } from '@/components/ui/Toast';
import { ThemeProvider } from '@/lib/ThemeContext';
import { SessionProvider } from '@/contexts/SessionContext';
import { AppFeatureProvider } from '@/lib/feature-runtime';
import { AgentProvider } from '@/contexts/AgentContext';
import AgentPanel, { AgentPanelProvider } from '@/components/agent/AgentPanel';
import RunDetailPanel, { RunDetailProvider } from '@/components/agent/RunDetailPanel';
import ReviewItemPanel, { ReviewPanelProvider } from '@/components/agent/review/ReviewItemPanel';
import { useAgentWebNotifications } from '@/lib/use-agent-web-notifications';
import { AnalyticsProvider } from '@/lib/analytics/AnalyticsProvider';

type Theme = 'light' | 'dark' | 'system';

function readCookie(name: string): string | null {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
    return match ? match[2] : null;
}

function readInitialTheme(): Theme {
    const v = readCookie('septima-theme');
    return v === 'light' || v === 'dark' || v === 'system' ? v : 'system';
}

// Headless bridge: subscribes the in-app browser-notification dispatcher to the
// app-wide agent-jobs feed. Renders nothing; mounted inside AgentProvider so its
// useAgent()/useSessionQuery() reads resolve. Runs across every staff view.
function WebNotificationBridge() {
    useAgentWebNotifications();
    return null;
}

export function RootLayout() {
    const initialTheme = readInitialTheme();
    // The two client-facing surfaces carry their own chrome and no agent
    // access: the client portal (/portal, PRD #71) and the public client
    // application (/apply, SEP-263). The agent panel's closed-state overlay
    // would otherwise trip a WCAG aria-hidden check, and neither surface has a
    // staff actor for the analytics capture to attribute.
    const { pathname } = useLocation();
    const isClientFacing = pathname.startsWith('/portal') || pathname.startsWith('/apply');

    return (
        <ToastProvider>
            <ThemeProvider initialTheme={initialTheme}>
                <SessionProvider>
                    <AppFeatureProvider>
                        <AgentProvider>
                            <WebNotificationBridge />
                            {/* Operational-analytics capture (issue #343) — staff only;
                            the portal is out of scope for Phase 1. */}
                            {!isClientFacing && <AnalyticsProvider />}
                            <RunDetailProvider>
                                <ReviewPanelProvider>
                                    <AgentPanelProvider>
                                        <div className="h-screen flex flex-col bg-[rgb(var(--color-page))] text-[rgb(var(--color-text-primary))]">
                                            <div className="flex-1 min-h-0 flex flex-col overflow-auto">
                                                <Outlet />
                                            </div>
                                        </div>
                                        {!isClientFacing && <AgentPanel />}
                                        {!isClientFacing && <RunDetailPanel />}
                                        {!isClientFacing && <ReviewItemPanel />}
                                    </AgentPanelProvider>
                                </ReviewPanelProvider>
                            </RunDetailProvider>
                        </AgentProvider>
                    </AppFeatureProvider>
                </SessionProvider>
            </ThemeProvider>
        </ToastProvider>
    );
}
